import { useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Car, GraduationCap, Music4, Plus } from 'lucide-react'

import { instrumentsApi, locationsApi, studentsApi, teachersApi } from '@/api/endpoints'
import type { StudentStatus } from '@/api/types'
import { useAuth } from '@/auth/AuthContext'
import { isTeacher } from '@/auth/permissions'
import { PageHeader } from '@/components/layout/AppLayout'
import { Alert, EmptyState } from '@/components/ui/Alert'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { InstrumentBadges, StudentStatusBadge } from '@/components/ui/InstrumentBadge'
import { Pagination } from '@/components/ui/Pagination'
import { SkeletonRows } from '@/components/ui/Spinner'
import {
  DataTable,
  EmptyRow,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@/components/ui/Table'
import { ToggleChip, Toolbar } from '@/components/ui/Toolbar'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { STUDENT_STATUS_LABELS } from '@/lib/dictionaries'
import { StudentFormModal } from './StudentFormModal'

const PAGE_SIZE = 20

const FILTER_SELECT =
  'cursor-pointer rounded-full border-2 border-transparent bg-surface-container-low px-4 py-2.5 text-sm font-semibold text-on-surface-variant transition-colors focus:border-primary focus:bg-surface focus:outline-none'

export function StudentsPage() {
  const { user } = useAuth()
  const teacherMode = isTeacher(user)

  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [locationId, setLocationId] = useState('')
  const [instrumentId, setInstrumentId] = useState('')
  const [teacherId, setTeacherId] = useState('')
  const [status, setStatus] = useState<StudentStatus | ''>('')
  const [includeArchived, setIncludeArchived] = useState(false)
  const [isFormOpen, setIsFormOpen] = useState(false)

  const debouncedSearch = useDebouncedValue(search)

  const query = useQuery({
    queryKey: [
      'students',
      {
        page,
        search: debouncedSearch,
        locationId,
        instrumentId,
        teacherId,
        status,
        includeArchived,
      },
    ],
    queryFn: () =>
      studentsApi.list({
        page,
        size: PAGE_SIZE,
        search: debouncedSearch || undefined,
        location_id: locationId || undefined,
        instrument_id: instrumentId || undefined,
        teacher_id: teacherId || undefined,
        status: status || undefined,
        include_archived: includeArchived,
      }),
    placeholderData: keepPreviousData,
  })

  const locationsQuery = useQuery({
    queryKey: ['locations', 'picker'],
    queryFn: () => locationsApi.list({ size: 200 }),
  })
  const instrumentsQuery = useQuery({
    queryKey: ['instruments'],
    queryFn: () => instrumentsApi.list(),
  })
  const teachersQuery = useQuery({
    queryKey: ['teachers', 'picker'],
    queryFn: () => teachersApi.list({ size: 200 }),
    enabled: !teacherMode,
  })

  const resetFilters = () => {
    setSearch('')
    setLocationId('')
    setInstrumentId('')
    setTeacherId('')
    setStatus('')
    setIncludeArchived(false)
    setPage(1)
  }

  const hasFilters =
    search !== '' ||
    locationId !== '' ||
    instrumentId !== '' ||
    teacherId !== '' ||
    status !== '' ||
    includeArchived

  const items = query.data?.items ?? []
  const isEmpty = !query.isPending && items.length === 0

  return (
    <>
      <PageHeader
        title="Uczniowie"
        description={
          teacherMode
            ? 'Twoi podopieczni. Możesz dopisać nowego ucznia — zostanie przypisany do Ciebie.'
            : 'Baza uczniów szkoły z podziałem na oddziały, instrumenty i prowadzących.'
        }
        breadcrumb={['Szkoła', 'Uczniowie']}
        action={
          <Button icon={<Plus size={18} />} onClick={() => setIsFormOpen(true)}>
            Nowy uczeń
          </Button>
        }
      />

      <Toolbar
        searchValue={search}
        onSearchChange={(value) => {
          setSearch(value)
          setPage(1)
        }}
        searchPlaceholder="Szukaj po nazwisku, telefonie lub opiekunie…"
      >
        {!teacherMode && (
          <select
            value={locationId}
            onChange={(event) => {
              setLocationId(event.target.value)
              setPage(1)
            }}
            aria-label="Filtr oddziału"
            className={FILTER_SELECT}
          >
            <option value="">Wszystkie oddziały</option>
            {(locationsQuery.data?.items ?? []).map((location) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </select>
        )}

        <select
          value={instrumentId}
          onChange={(event) => {
            setInstrumentId(event.target.value)
            setPage(1)
          }}
          aria-label="Filtr instrumentu"
          className={FILTER_SELECT}
        >
          <option value="">Wszystkie instrumenty</option>
          {(instrumentsQuery.data ?? []).map((instrument) => (
            <option key={instrument.id} value={instrument.id}>
              {instrument.name}
            </option>
          ))}
        </select>

        {!teacherMode && (
          <select
            value={teacherId}
            onChange={(event) => {
              setTeacherId(event.target.value)
              setPage(1)
            }}
            aria-label="Filtr nauczyciela"
            className={FILTER_SELECT}
          >
            <option value="">Wszyscy nauczyciele</option>
            {(teachersQuery.data?.items ?? []).map((item) => (
              <option key={item.user.id} value={item.user.id}>
                {item.user.full_name}
              </option>
            ))}
          </select>
        )}

        <select
          value={status}
          onChange={(event) => {
            setStatus(event.target.value as StudentStatus | '')
            setPage(1)
          }}
          aria-label="Filtr statusu"
          className={FILTER_SELECT}
        >
          <option value="">Każdy status</option>
          {Object.entries(STUDENT_STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>

        <ToggleChip
          checked={includeArchived}
          onChange={(checked) => {
            setIncludeArchived(checked)
            setPage(1)
          }}
          label="Pokaż archiwalnych"
        />

        {hasFilters && (
          <button
            type="button"
            onClick={resetFilters}
            className="text-sm font-bold text-primary transition-opacity hover:opacity-70"
          >
            Wyczyść filtry
          </button>
        )}
      </Toolbar>

      {query.isError && (
        <Alert tone="error" className="mb-6">
          Nie udało się wczytać listy uczniów. Odśwież stronę i spróbuj ponownie.
        </Alert>
      )}

      <Card>
        {query.isPending ? (
          <SkeletonRows />
        ) : isEmpty ? (
          <EmptyState
            icon={<Music4 size={28} />}
            title={hasFilters ? 'Brak wyników' : 'Nie ma jeszcze żadnego ucznia'}
            description={
              hasFilters
                ? 'Zmień kryteria wyszukiwania albo wyczyść filtry.'
                : 'Dodaj pierwszego ucznia, aby móc planować mu zajęcia i rozliczać obecności.'
            }
            action={
              hasFilters ? (
                <Button variant="outline" onClick={resetFilters}>
                  Wyczyść filtry
                </Button>
              ) : (
                <Button icon={<Plus size={18} />} onClick={() => setIsFormOpen(true)}>
                  Nowy uczeń
                </Button>
              )
            }
          />
        ) : (
          <>
            <DataTable caption="Lista uczniów szkoły">
              <TableHead
                columns={['Uczeń', 'Instrument', 'Nauczyciel', 'Oddział', 'Kontakt', 'Status']}
              />
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id} muted={item.status === 'ARCHIVED'}>
                    <TableCell>
                      <Link
                        to={`/uczniowie/${item.id}`}
                        className="flex items-center gap-3 rounded-lg transition-opacity hover:opacity-75"
                      >
                        <Avatar name={item.full_name} />
                        <div>
                          <p className="font-bold text-on-surface">{item.full_name}</p>
                          {item.is_traveling && (
                            <span className="inline-flex items-center gap-1 text-xs text-on-surface-variant">
                              <Car size={12} aria-hidden="true" />
                              Zajęcia z dojazdem
                            </span>
                          )}
                        </div>
                      </Link>
                    </TableCell>
                    <TableCell>
                      <InstrumentBadges instruments={item.instruments} />
                    </TableCell>
                    <TableCell>
                      {item.teacher ? (
                        <span className="inline-flex items-center gap-1.5 text-sm">
                          <GraduationCap size={14} aria-hidden="true" />
                          {item.teacher.full_name}
                        </span>
                      ) : (
                        <span className="text-xs text-on-surface-variant">Nie przypisano</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{item.location.name}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-on-surface-variant">
                        {item.phone ?? item.guardian_phone ?? '—'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <StudentStatusBadge status={item.status} />
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && <EmptyRow colSpan={6} message="Brak uczniów." />}
              </TableBody>
            </DataTable>

            <Pagination
              page={page}
              size={PAGE_SIZE}
              total={query.data?.total ?? 0}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>

      <StudentFormModal open={isFormOpen} student={null} onClose={() => setIsFormOpen(false)} />
    </>
  )
}
